// The storefront canvas: a store's shopping assistant beside the chat that designs it, the way an App sits beside
// the chat that builds it. It shows the newest version in the store's real preview (its own greeting and products),
// moves to each version Chat saves, and offers the App canvas's Select: pointing at a part of the assistant puts it
// in the composer for the next message. Versions are drafts until someone publishes on the Storefront page; the
// Versions view lists them all and compares any one with the live one.
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy from '@/i18n/ns/storefront';
import { Segmented } from '@/components/ui/kit';
import { useOptionalWorkspace } from '@/workspace/context';
import { AppCanvasTools, SelectHint } from '../apps/preview/live-build';
import { CanvasHeaderLeadTools } from '../canvas-footer';
import { CompanyRequestError } from '@/company/api';
import { EmptyState, ErrorState } from '../primitives';
import { readStorefrontSelection } from './selection';
import { useStorefrontApi } from './api';
import { useRemote } from './use-storefront';
import { MarketFilter } from './market-filter';
import { PhotoCheck } from './photo-check';
import { readPhotoFacts, type PhotoFact } from './photos';
import { VersionsView } from './versions-view';
import { LookCompare } from './look-compare';
import { designPreviewUrl } from './widget-tab';

// Each saved design is announced (app.tsx, from the tool's result) so an open canvas moves to the new version.
const saved = new Map<string, number>();
const savedListeners = new Set<() => void>();
const onSaved = (listener: () => void) => {
  savedListeners.add(listener);
  return () => void savedListeners.delete(listener);
};
export function announceStorefrontDesign(storeId: string) {
  saved.set(storeId, (saved.get(storeId) ?? 0) + 1);
  savedListeners.forEach((listener) => listener());
}
const useSavedTick = (storeId: string) =>
  useSyncExternalStore(
    onSaved,
    () => saved.get(storeId) ?? 0,
    () => 0,
  );

// "Updating…" while a thread's chat is changing a design (storefront-design-observer.tsx, inside the thread runtime).
const updating = new Map<string, boolean>();
const updatingListeners = new Set<() => void>();
const onUpdating = (listener: () => void) => {
  updatingListeners.add(listener);
  return () => void updatingListeners.delete(listener);
};
export function publishUpdating(threadId: string, value: boolean) {
  if ((updating.get(threadId) ?? false) === value) return;
  updating.set(threadId, value);
  updatingListeners.forEach((listener) => listener());
}
const useUpdating = (threadId: string | null | undefined) =>
  useSyncExternalStore(
    onUpdating,
    () => (threadId ? (updating.get(threadId) ?? false) : false),
    () => false,
  );

/** Select, as in the App canvas: the frame outlines what the pointer is over; a click becomes the composer's chip. */
function useStorefrontSelect(storeId: string, send: (message: unknown) => void) {
  const ws = useOptionalWorkspace();
  const [selecting, setSelecting] = useState(false);
  const latest = useRef({ ws, selecting });
  latest.current = { ws, selecting };
  const chosen = Boolean(ws?.turnContext.storefrontSelection);
  useEffect(() => {
    // The inspector's own hue (--pc-insp) in the current theme, drawn inside the preview; the frame's default without it.
    const style = getComputedStyle(document.querySelector('.ws-canvas') ?? document.body);
    const color =
      typeof style?.getPropertyValue === 'function'
        ? style.getPropertyValue('--pc-insp').trim()
        : '';
    send({ type: 'poneva.inspect', on: selecting, ...(color ? { color } : {}) });
  }, [selecting, send]);
  // A sent or removed selection takes its outline with it.
  useEffect(() => {
    if (!chosen) send({ type: 'poneva.inspect', clear: true });
  }, [chosen, send]);
  const receive = useCallback(
    (data: { on?: unknown; selected?: unknown }) => {
      const { ws: current, selecting: on } = latest.current;
      if (data.on === false) setSelecting(false);
      if (!on || data.selected === undefined || !current) return;
      const selection = readStorefrontSelection(storeId, data.selected);
      if (!selection) return;
      current.setTurnContext({ ...current.turnContext, storefrontSelection: selection });
      setSelecting(false);
      // The person says what to change next.
      requestAnimationFrame(() =>
        document.querySelector<HTMLTextAreaElement>('.ws-chat-slot .aui-composer-input')?.focus(),
      );
    },
    [storeId],
  );
  return { selecting, setSelecting, receive };
}

export function StorefrontCanvas({ storeId }: { storeId: string }) {
  const copy = useNamespaceCopy(storefrontCopy);
  const ws = useOptionalWorkspace();
  // Design is the newest version with Select; Versions lists every version (one toolbar, as in the design).
  const [view, setView] = useState<'design' | 'compare' | 'versions'>('design');
  const api = useStorefrontApi();
  const tick = useSavedTick(storeId);
  const [polling, setPolling] = useState(true);
  // A link to an assistant this workspace does not have (another workspace's, or removed) loads
  // as null, so the canvas says where the assistant is not rather than that the preview failed.
  const remote = useRemote(
    () =>
      api.get(storeId).then(
        (answer) => answer.store,
        (failure: unknown) => {
          if (failure instanceof CompanyRequestError && failure.code === 'store_not_found')
            return null;
          throw failure;
        },
      ),
    `${storeId}:${tick}`,
    polling,
  );
  const store = remote.data;
  // A read can finish after this canvas opens without a Chat design-save event.
  // Reuse the Storefront page's serialized polling; terminal states stop it.
  const runActive = Boolean(
    store?.run && !['ready', 'failed', 'cancelled', 'held'].includes(store.run.status),
  );
  useEffect(
    () => setPolling(!remote.error && (remote.loading || store?.status === 'reading' || runActive)),
    [remote.loading, remote.error, store?.status, runActive],
  );
  const version = store?.draft?.version ?? store?.live?.version ?? 0;
  // With several markets the preview shows one at a time: that market's own site and prices, in
  // its language's words (design.translations). Only a market whose checks passed has a preview;
  // one still found or checking is added through Chat first.
  const verified = store?.markets?.filter((entry) => entry.status === 'ready') ?? [];
  const markets = verified.length > 1 ? verified : [];
  const [previewMarket, setPreviewMarket] = useState<string | null>(null);
  const shown = markets.find((entry) => entry.code === previewMarket) ?? markets[0] ?? null;
  // A saved design is a new version: the preview follows the version the store now has.
  const preview = useRemote(
    version ? () => api.preview(storeId, null, shown?.code ?? null) : null,
    `${storeId}:${version}:${shown?.code ?? ''}`,
  );
  // Opening design while the first read runs keeps the same branded example preview
  // already shown in Widget. A saved version always uses its authenticated real preview.
  const baseSrc = version
    ? (preview.data?.frameUrl ?? null)
    : store
      ? designPreviewUrl(store, 'computer')
      : null;
  const language = shown?.languages[0] ?? null;
  const src =
    baseSrc && language
      ? `${baseSrc}${baseSrc.includes('#') ? '&' : '#'}lang=${encodeURIComponent(language)}`
      : baseSrc;
  const origin = useMemo(() => {
    try {
      return src ? new URL(src, location.href).origin : null;
    } catch {
      return null;
    }
  }, [src]);
  const frame = useRef<HTMLIFrameElement>(null);
  const send = useCallback(
    (message: unknown) => {
      if (origin) frame.current?.contentWindow?.postMessage(message, origin);
    },
    [origin],
  );
  const select = useStorefrontSelect(storeId, send);
  // Select waits for the frame to listen (it says so once it is ready); a new version is a new frame.
  const [ready, setReady] = useState(false);
  // Leaving Design takes the preview away; back in Design, Select waits for the new frame to listen.
  useEffect(() => {
    if (view !== 'design') setReady(false);
  }, [view]);
  // How the product photos in the preview's newest answer look; a new version starts without.
  const [photos, setPhotos] = useState<PhotoFact[] | null>(null);
  useEffect(() => {
    select.setSelecting(false);
    setReady(false);
    setPhotos(null);
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps
  // "Fix with Chat" drafts the request in this chat, the photos' facts attached as the selected part.
  const fixPhotos = (facts: string) =>
    ws?.send(
      copy.photosPrompt,
      {
        ...ws.turnContext,
        storefrontSelection: {
          store: storeId,
          part: '.sa-img (product image)',
          tag: 'img',
          text: facts,
        },
      },
      false,
    );
  // "Fix with Chat" for a colour near the shop's own drafts the request in this chat, both colours named.
  const fixLook = (prompt: string) => ws?.send(prompt, ws.turnContext, false);
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow || event.origin !== origin) return;
      const data = event.data as {
        type?: unknown;
        on?: unknown;
        selected?: unknown;
        ready?: unknown;
        photos?: unknown;
      } | null;
      if (data?.type !== 'poneva.inspect') return;
      if (data.ready === true) {
        setReady(true);
        // Hello: the preview now knows where to report, also before anyone uses Select.
        frame.current?.contentWindow?.postMessage(
          { type: 'poneva.inspect', on: false },
          origin ?? '',
        );
      } else if (data.photos !== undefined) setPhotos(readPhotoFacts(data.photos));
      else select.receive(data);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin, select.receive]); // eslint-disable-line react-hooks/exhaustive-deps
  const changing = useUpdating(ws?.threadId);
  if (!remote.loading && !remote.error && remote.data === null)
    return (
      <div data-testid="sf-canvas-missing">
        <EmptyState
          icon="store"
          title={copy.canvasMissingTitle}
          description={copy.canvasMissingBody}
        >
          <a className="ws-text-button" href="#/workspace/storefront">
            {copy.canvasMissingLink}
          </a>
        </EmptyState>
      </div>
    );
  if (remote.error || preview.error)
    return (
      <ErrorState
        message={copy.widgetFailed}
        onRetry={() => {
          // A market whose preview failed falls back to the store's own, so Retry can succeed.
          setPreviewMarket(null);
          remote.refresh();
          preview.refresh();
        }}
      />
    );
  const live = store?.live?.version ?? null;
  const draft = store?.draft?.version ?? null;
  const publishHref = `#/workspace/storefront?store=${encodeURIComponent(storeId)}&tab=install`;
  const versions = view === 'versions' && version > 0 && store;
  const compare = view === 'compare' && version > 0 && store && store.brand ? store : null;
  return (
    <div className="sf-canvas" data-testid="sf-canvas" data-view={view}>
      {version > 0 && (
        <CanvasHeaderLeadTools>
          <Segmented
            label={copy.viewLabel}
            options={[
              { value: 'design', label: copy.viewDesign },
              // Only a store whose read kept the shop's own look has something to compare with.
              ...(store?.brand ? [{ value: 'compare' as const, label: copy.viewCompare }] : []),
              { value: 'versions', label: `${copy.viewVersions} ${version}` },
            ]}
            value={view}
            onChange={(next) => {
              select.setSelecting(false);
              setView(next);
            }}
          />
        </CanvasHeaderLeadTools>
      )}
      <AppCanvasTools
        selecting={select.selecting}
        onSelecting={select.setSelecting}
        canSelect={view === 'design' && Boolean(src) && ready}
        updating={changing}
        hint={copy.canvasSelectHint}
      />
      {view === 'design' && shown && (
        <CanvasHeaderLeadTools>
          <MarketFilter
            markets={markets}
            value={shown.code}
            onChange={setPreviewMarket}
            withAll={false}
          />
        </CanvasHeaderLeadTools>
      )}
      {store && (
        <p className="sf-canvas-status" data-testid="sf-canvas-status">
          <b>{store.name}</b>
          <span>
            {draft && draft !== live
              ? live
                ? formatCopy(copy.canvasDraftOverLive, {
                    version: String(draft),
                    live: String(live),
                  })
                : formatCopy(copy.canvasDraft, { version: String(draft) })
              : live
                ? formatCopy(copy.canvasLive, { version: String(live) })
                : copy.canvasNoVersion}
          </span>
          {draft && draft !== live && (
            <a href={publishHref} className="sf-canvas-publish" data-testid="sf-canvas-publish">
              {copy.canvasPublish}
            </a>
          )}
        </p>
      )}
      {compare ? (
        <LookCompare store={compare} version={version} onFix={fixLook} />
      ) : versions ? (
        <VersionsView
          store={store}
          publishHref={publishHref}
          onRestored={() => announceStorefrontDesign(storeId)}
        />
      ) : (
        <>
          {select.selecting && <SelectHint hint={copy.canvasSelectHint} />}
          <div className="sf-canvas-stage">
            {src ? (
              <iframe
                ref={frame}
                key={src}
                className="sf-canvas-frame"
                src={src}
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                title={formatCopy(copy.canvasFrameTitle, { name: store?.name ?? '' })}
                data-testid="sf-canvas-frame"
              />
            ) : store && !version ? (
              // No version yet (the store is still being read, or reading stopped): say so, never load forever.
              <p className="sf-canvas-empty" data-testid="sf-canvas-empty">
                {copy.canvasNoVersion}
              </p>
            ) : (
              <div className="sf-canvas-frame sf-design-proposal-loading" aria-hidden="true" />
            )}
          </div>
          {photos && photos.length > 0 && <PhotoCheck photos={photos} onFix={fixPhotos} />}
        </>
      )}
    </div>
  );
}
